import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, FormHelperText, Typography } from '@mui/material';
import { loadGoogleMapsPlaces } from '../utils/loadGoogleMaps';

function normalizeComponents(components) {
  return (components || []).map((c) => ({
    types: c?.types || [],
    long: c?.long_name || c?.longText || c?.longText?.toString?.() || '',
    short: c?.short_name || c?.shortText || c?.shortText?.toString?.() || '',
  }));
}

function pickComponent(components, type) {
  return (components || []).find((c) => Array.isArray(c.types) && c.types.includes(type)) || null;
}

function getCity(components) {
  // Prefer locality, fallback to postal_town, then sublocality.
  const locality = pickComponent(components, 'locality');
  if (locality?.long_name) return locality.long_name;
  const postalTown = pickComponent(components, 'postal_town');
  if (postalTown?.long_name) return postalTown.long_name;
  const sublocality = pickComponent(components, 'sublocality') || pickComponent(components, 'sublocality_level_1');
  if (sublocality?.long_name) return sublocality.long_name;
  return '';
}

function parsePlace(place) {
  const rawComponents = place?.address_components || place?.addressComponents || [];
  const comps = normalizeComponents(rawComponents);

  const streetNumber = pickComponent(comps, 'street_number')?.long || '';
  const route = pickComponent(comps, 'route')?.long || '';
  const subpremise = pickComponent(comps, 'subpremise')?.long || '';
  const city = getCity(comps.map((c) => ({ types: c.types, long_name: c.long })));
  const state = pickComponent(comps, 'administrative_area_level_1')?.short || '';
  const zip = pickComponent(comps, 'postal_code')?.long || '';
  const zipSuffix = pickComponent(comps, 'postal_code_suffix')?.long || '';

  const addressLine1 = [streetNumber, route].filter(Boolean).join(' ').trim();
  const unit = subpremise ? `#${subpremise}` : '';
  let address = [addressLine1, unit].filter(Boolean).join(' ').trim();

  const location = place?.geometry?.location || place?.location || null;
  const latitude =
    typeof location?.lat === 'function'
      ? location.lat()
      : (typeof location?.lat === 'number' ? location.lat : null);
  const longitude =
    typeof location?.lng === 'function'
      ? location.lng()
      : (typeof location?.lng === 'number' ? location.lng : null);

  const formatted =
    place?.formatted_address || place?.formattedAddress || null;

  // Fallback: if we didn't get components (common unless fields are fetched),
  // derive street from formatted address.
  if (!address && formatted) {
    address = formatted.split(',')[0].trim();
  }

  return {
    address,
    city,
    state,
    zipCode: zipSuffix ? `${zip}-${zipSuffix}` : zip,
    formattedAddress: formatted,
    placeId: place?.place_id || place?.placeId || place?.id || null,
    latitude,
    longitude,
  };
}

export default function AddressAutocompleteField({
  label = 'Street Address',
  value, // kept for backwards compatibility (used only as display fallback)
  onChange, // kept for backwards compatibility
  onPlaceSelected,
  disabled,
  error,
  helperText,
  required,
}) {
  const containerRef = useRef(null);
  const elementRef = useRef(null);
  const inputHandlersRef = useRef([]);
  const callbacksRef = useRef({ onChange: null, onPlaceSelected: null });
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Ensure the Google Places dropdown renders above MUI Dialogs (z-index 1300).
  useEffect(() => {
    const styleId = 'pac-container-zindex-fix';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = '.pac-container { z-index: 2100 !important; }';
      document.head.appendChild(style);
    }
  }, []);

  // Keep latest callbacks without forcing widget re-init.
  useEffect(() => {
    callbacksRef.current = { onChange, onPlaceSelected };
  }, [onChange, onPlaceSelected]);

  const effectiveHelperText = useMemo(() => {
    if (helperText) return helperText;
    if (loadError) return 'Autocomplete unavailable (missing/invalid Maps key).';
    return 'Start typing and pick an address suggestion.';
  }, [helperText, loadError]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        await loadGoogleMapsPlaces();
        if (cancelled) return;

        // Use Places API (New) widget: PlaceAutocompleteElement.
        // This avoids deprecated google.maps.places.Autocomplete for new projects/keys.
        const lib = await window.google.maps.importLibrary('places');
        if (cancelled) return;

        const PlaceAutocompleteElement =
          lib?.PlaceAutocompleteElement || window.google?.maps?.places?.PlaceAutocompleteElement;
        if (!PlaceAutocompleteElement) {
          throw new Error('PlaceAutocompleteElement is unavailable (Places library not loaded).');
        }

        const el = new PlaceAutocompleteElement();
        // Match our use case (address entry)
        if (typeof el.setFields === 'function') {
          el.setFields(['address_components', 'geometry', 'formatted_address', 'place_id']);
        }

        el.style.display = 'block';
        el.style.width = '100%';
        // Give the widget a predictable stacking context in dialogs
        el.style.zIndex = '2000';

        el.addEventListener('gmp-error', () => {
          setLoadError(new Error('Places request denied (check API key / API enablement).'));
          setReady(false);
        });

        // Keep caller state in sync with what the user types, even if they
        // don't pick a suggestion. (Best-effort: depends on widget exposing
        // a `value` property.)
        const readTextValue = (evt) => {
          if (evt && typeof evt.target?.value === 'string') return evt.target.value;
          if (typeof el.value === 'string') return el.value;
          try {
            const shadowInput = el.shadowRoot?.querySelector('input');
            if (shadowInput && typeof shadowInput.value === 'string') return shadowInput.value;
          } catch (e) {}
          const attr = el.getAttribute && el.getAttribute('value');
          return typeof attr === 'string' ? attr : '';
        };

        const handleTypedInput = (evt) => {
          const cb = callbacksRef.current?.onChange;
          if (!cb) return;
          const txt = readTextValue(evt);
          cb({ target: { value: txt } });
        };

        // Always attach; callback ref determines whether it does anything.
        el.addEventListener('input', handleTypedInput);
        el.addEventListener('change', handleTypedInput);
        inputHandlersRef.current = [
          ['input', handleTypedInput],
          ['change', handleTypedInput],
        ];

        el.addEventListener('gmp-select', async (evt) => {
          try {
            // Preferred path for Places API (New):
            // event.placePrediction -> toPlace() -> fetchFields() for components.
            const prediction =
              evt?.placePrediction ||
              evt?.detail?.placePrediction ||
              evt?.detail?.prediction ||
              null;

            if (prediction && typeof prediction.toPlace === 'function') {
              const place = prediction.toPlace();
              if (place?.fetchFields) {
                await place.fetchFields({
                  fields: ['addressComponents', 'location', 'formattedAddress', 'id'],
                });
              }
              const parsed = parsePlace(place);
              const cb = callbacksRef.current?.onPlaceSelected;
              if (cb) cb(parsed);
              return;
            }

            // Fallback: widget-provided place details (may be partial).
            const selected =
              (typeof el.getPlace === 'function' && el.getPlace()) ||
              evt?.place ||
              evt?.detail?.place ||
              null;

            if (selected) {
              const parsed = parsePlace(selected);
              const cb = callbacksRef.current?.onPlaceSelected;
              if (cb) cb(parsed);
            }
          } catch (err) {
            // keep quiet but surface as helper text
            setLoadError(err);
          }
        });

        // Mount
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
          containerRef.current.appendChild(el);
          elementRef.current = el;
        }

        setReady(true);
      } catch (err) {
        setLoadError(err);
        setReady(false);
      }
    }

    init();

    return () => {
      cancelled = true;
      try {
        if (elementRef.current && inputHandlersRef.current.length) {
          inputHandlersRef.current.forEach(([type, handler]) => {
            try {
              elementRef.current.removeEventListener(type, handler);
            } catch (e) {}
          });
        }
        if (elementRef.current?.remove) {
          elementRef.current.remove();
        }
      } catch (e) {}
    };
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mb: 0.5, color: error ? 'error.main' : 'text.secondary' }}
        >
          {label}{required ? ' *' : ''}
        </Typography>
      )}

      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          // Basic framing so it doesn't look out-of-place next to MUI fields
          border: '1px solid',
          borderColor: error ? 'error.main' : 'rgba(0,0,0,0.23)',
          borderRadius: 1,
          px: 1,
          py: 1,
          backgroundColor: 'background.paper',
        }}
      />

      <FormHelperText error={!!error} sx={{ mt: 0.5 }}>
        {effectiveHelperText}
        {!ready && value ? ` (current: ${value})` : ''}
      </FormHelperText>
    </Box>
  );
}


